import React from "react";
import Container from "../components/common/Container";
import MetaTags from "../components/common/MetaTags";
import StaticHeaderImage from "../assets/img/common/static-pages-header.png";
import { HiGlobeAlt, HiUserGroup, HiLightningBolt, HiShieldCheck, HiCube, HiSparkles, HiLink } from "react-icons/hi";
import { FaTwitter, FaLinkedin, FaGithub } from "react-icons/fa";
import { Link } from "react-router-dom";

const teamMembers = [
  {
    name: "Seyed Mojtaba Hosseini, Ph.D",
    role: "Co-Founder / Project Manager",
    affiliation: "Masaryk University",
    twitter: "https://x.com/smhbamakan",
    linkedin: "https://www.linkedin.com/in/smhbamakan/",
  },
  {
    name: "Mohammadsadegh Solouki",
    role: "Co-Founder / Product Manager",
    affiliation: "Canadian University Dubai",
    linkedin: "https://www.linkedin.com/in/mohamadsolouki/",
    github: "https://github.com/mohamadsolouki",
  },
  {
    name: "Amirhoseein Azarpour",
    role: "Tech Lead",
    affiliation: "",
    twitter: "https://x.com/7Amirhossein8",
    github: "https://github.com/AmirH-A",
  },
  {
    name: "Swift Evo",
    role: "Business Development Manager",
    affiliation: "",
    twitter: "https://x.com/swiftevo1",
    github: "https://github.com/Swiftevo",
  },
];

const goals = [
  {
    icon: HiLightningBolt,
    title: "Accessible",
    description: "Facilitating progression of scientific research via accessible funding",
    gradient: "from-primary to-[#7e7cff]",
  },
  {
    icon: HiUserGroup,
    title: "Network",
    description: "Establishing a network of researchers and investors with shared interests",
    gradient: "from-primary-green to-[#0dcada]",
  },
  {
    icon: HiCube,
    title: "Marketplace",
    description: "Fostering a value chain within the scientific community through science marketplace",
    gradient: "from-[#7e7cff] to-primary",
  },
  {
    icon: HiShieldCheck,
    title: "Transparency",
    description: "Utilizing blockchain technology for transparency and accountability in funding",
    gradient: "from-primary to-primary-green",
  },
  {
    icon: HiSparkles,
    title: "Opportunities",
    description: "Collaborating with organizations to provide funding opportunities to qualified scientists",
    gradient: "from-[#0dcada] to-primary-green",
  },
  {
    icon: HiGlobeAlt,
    title: "Collaboration",
    description: "Promoting collaboration, innovation, and convergence of academia and industry",
    gradient: "from-primary-green to-primary",
  },
];

const features = [
  {
    icon: HiSparkles,
    title: "Diversified Impact",
    description: "NFT-Fundraising transcends sectors, fostering collaboration in scientific research, charity, arts, tech, and more. It's an inclusive approach that empowers contributors across various fields.",
  },
  {
    icon: HiGlobeAlt,
    title: "Global Reach",
    description: "Leveraging blockchain technology, NFT-Fundraising transcends geographical boundaries, enabling global participation in initiatives that resonate with individuals worldwide.",
  },
  {
    icon: HiCube,
    title: "Decentralization",
    description: "NFT-Fundraising operates on decentralized blockchain networks, reducing intermediaries and empowering direct interaction between contributors and creators.",
  },
];

const traditionalProblems = [
  "Current crowdfunding platforms for science are not specifically built for scientific projects",
  "Difficulty for early-career researchers or less recognized institutions to get funding",
  "Intellectual Property (IP) handling is often complicated and opaque",
  "Gender, Racial, and Ethnic discrimination in grant allocations",
  "Lack of access and transparency in the allocation of funds",
  "Limited number of calls per year",
  "Lack of governmental grants",
  "Long evaluation process",
];

const solutions = [
  "Democratizes the funding process",
  "Ensures transparency and accessibility",
  "Potential for Return on Investment (ROI) in NFT Science Marketplace",
  "Facilitates the ownership and transfer of IP rights through NFTs",
];

const TeamMemberCard = ({ member }) => {
  return (
    <div className="group bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-primary/30 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1">
      {/* Avatar placeholder */}
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-primary-green flex items-center justify-center">
        <span className="text-2xl font-bold text-white">
          {member.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
        </span>
      </div>
      
      <h3 className="text-lg font-semibold text-secondary text-center mb-1">
        {member.name}
      </h3>
      <p className="text-primary text-sm text-center mb-1">
        {member.role}
      </p>
      {member.affiliation && (
        <p className="text-gray-500 text-xs text-center mb-4">
          {member.affiliation}
        </p>
      )}
      
      {/* Social Links */}
      <div className="flex justify-center gap-3 mt-4">
        {member.twitter && (
          <a
            href={member.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-primary hover:text-white transition-all duration-300"
          >
            <FaTwitter size={14} />
          </a>
        )}
        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-primary hover:text-white transition-all duration-300"
          >
            <FaLinkedin size={14} />
          </a>
        )}
        {member.github && (
          <a
            href={member.github}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-primary hover:text-white transition-all duration-300"
          >
            <FaGithub size={14} />
          </a>
        )}
      </div>
    </div>
  );
};

const About = () => {
  return (
    <div className="w-full flex flex-col items-center pt-32">
      <MetaTags
        title="About Us"
        description="FunDeSci is a decentralized platform that revolutionizes scientific funding, connecting researchers, investors, and organizations to drive meaningful advancements in research."
        link="/about"
      />

      {/* Hero Section */}
      <section
        style={{
          backgroundImage: `url(${StaticHeaderImage})`,
        }}
        className="w-full flex flex-col justify-center items-center h-64 bg-[rgba(30,21,65,0.85)] bg-no-repeat bg-cover bg-blend-multiply bg-center"
      >
        <span className="text-primary-green text-sm font-medium mb-2 uppercase tracking-wider">
          NFT-Fundraising for DeSci
        </span>
        <h1 className="text-4xl sm:text-5xl text-center abel-font font-bold text-white tracking-wider mb-4">
          About FunDeSci
        </h1>
        <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-3">
          <span className="text-white/70 text-sm font-medium">Powered by</span>
          <span className="text-white font-bold text-base">DeSci Asia</span>
        </div>
        <p className="text-white/70 max-w-2xl text-center px-4">
          A decentralized platform revolutionizing scientific funding through blockchain technology
        </p>
      </section>

      {/* Who We Are Section */}
      <section className="w-full py-20 bg-gradient-to-b from-white to-whitesmoke">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Who We Are
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-secondary abel-font mb-6">
                Revolutionizing Scientific Funding
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                FunDeSci is a decentralized platform that revolutionizes scientific funding, connecting researchers, investors, and organizations to drive meaningful advancements in research. Through blockchain technology and NFTs, we provide a transparent, accessible, and efficient ecosystem, solving the challenges of traditional funding models.
              </p>
              <div className="flex items-center gap-3 mb-6 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary-green/5 border border-primary/10">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-primary/20">
                  <span className="text-gray-600 text-sm font-medium">Backed by</span>
                  <span className="text-primary font-bold text-base">DeSci Asia</span>
                </div>
                <p className="text-gray-600 text-sm flex-1">
                  Supported by the leading decentralized science community in Asia
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed">
                FunDeSci is at the forefront of NFT-fundraising, providing a secure and transparent platform for researchers and investors. Adding an additional dimension to the FunDeSci platform by integrating a marketplace for research-based NFTs. Our team's expertise in blockchain, software development, and scientific research positions us to drive success and create a lasting impact in the scientific funding landscape.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
                >
                  <feature.icon className="text-primary mb-4" size={32} />
                  <h3 className="text-lg font-semibold text-secondary mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Our Goals Section */}
      <section className="w-full py-20 bg-gradient-to-b from-whitesmoke to-white">
        <Container>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Our Mission
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary abel-font mb-4">
              Our Goals
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our core values are deeply ingrained in everything we do, and they drive our commitment to excellence and customer satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal, index) => (
              <div
                key={index}
                className="group bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:border-primary/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${goal.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <goal.icon className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-secondary mb-2 abel-font">
                  {goal.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {goal.description}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Problems & Solutions Section */}
      <section className="w-full py-20 bg-gradient-to-br from-secondary via-[#2a1f52] to-[#1a1235]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Problems */}
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-red-500/10 text-red-400 text-sm font-medium mb-4">
                The Challenge
              </span>
              <h2 className="text-3xl font-bold text-white abel-font mb-6">
                Traditional Funding Problems
              </h2>
              <ul className="space-y-3">
                {traditionalProblems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3 text-white/70">
                    <span className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
                    {problem}
                  </li>
                ))}
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-primary-green/10 text-primary-green text-sm font-medium mb-4">
                Our Solution
              </span>
              <h2 className="text-3xl font-bold text-white abel-font mb-6">
                How FunDeSci Solves It
              </h2>
              <p className="text-white/70 mb-6">
                FunDeSci leverages blockchain technology and NFTs to revolutionize scientific research funding:
              </p>
              <ul className="space-y-4">
                {solutions.map((solution, index) => (
                  <li key={index} className="flex items-start gap-3 text-white">
                    <span className="w-6 h-6 rounded-full bg-primary-green flex items-center justify-center flex-shrink-0 mt-0.5">
                      <HiShieldCheck className="text-white" size={14} />
                    </span>
                    {solution}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* Team Section */}
      <section className="w-full py-20 bg-gradient-to-b from-white to-whitesmoke">
        <Container>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Our Team
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary abel-font mb-4">
              Meet Our Team
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Where curiosity meets innovation, and the boundaries of knowledge are continuously pushed forward.
            </p>
          </div>

          {/* Core Team - 4 members centered */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <TeamMemberCard key={index} member={member} />
            ))}
          </div>
        </Container>
      </section>

      {/* Partnership Section */}
      <section className="w-full py-20 bg-gradient-to-b from-whitesmoke to-white">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#1E1541] via-[#2a1f52] to-[#1a1235] rounded-3xl p-10 md:p-14 border border-primary/20 shadow-2xl shadow-primary/10 relative overflow-hidden">
              {/* Decorative blobs */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-green/10 rounded-full blur-[60px] pointer-events-none"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-5">
                    <HiLink className="text-primary" size={16} />
                    <span className="text-primary text-sm font-medium">For Web3 Projects</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white abel-font mb-4">
                    Partner With FunDeSci
                  </h2>
                  <p className="text-white/70 leading-relaxed mb-4">
                    Are you building in the Web3 space and looking for collaboration opportunities? We're actively seeking partnerships with DeFi protocols, DAOs, NFT platforms, and other Web3 projects that share our vision of making science more open, funded, and impactful.
                  </p>
                  <ul className="space-y-2 mb-8">
                    {[
                      "Co-marketing & cross-promotion",
                      "Technical integrations & joint grants",
                      "Community growth & governance collaboration",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-green flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="https://airtable.com/appgIUJ9M6QYpvlnx/pagsR74puDpO6xvnP/form"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-primary-green text-white font-semibold rounded-xl hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-primary/30"
                  >
                    <HiUserGroup size={20} />
                    Apply for Partnership
                  </a>
                </div>

                {/* Icon decoration */}
                <div className="hidden md:flex flex-col items-center gap-4 flex-shrink-0">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary to-primary-green flex items-center justify-center shadow-xl shadow-primary/30">
                    <HiLink className="text-white" size={52} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="w-full py-20 bg-gradient-to-br from-primary via-[#7e7cff] to-primary-green">
        <Container>
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white abel-font mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">
              Find out more by exploring our application and start your journey in decentralized science funding.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/projects"
                className="px-8 py-4 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 transition-all duration-300"
              >
                Explore Projects
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default About;
